import React, { ReactElement, useMemo } from 'react'
import { Box, Flex } from 'theme-ui'
import CustomRulesTitle from 'components/Dashboard/Profiles/CustomRules/CustomRulesTitle'
import BackButton from 'components/Dashboard/Profiles/CustomRules/Buttons/BackButton'
import { useAppSelector } from 'store/hooks'
import { RuleType } from 'store/api/rules/rules.interface'
import BlockFolder from 'images/blockfolder.svg'
import RedirectFolder from 'images/redirectfolder.svg'
import BypassFolder from 'images/bypassfolder.svg'
import NoRuleFolder from 'images/folder-icon-small.svg'
import { Svg } from 'ui'
import { useGetProxiesQuery } from 'store/api/proxies'
import MagicFolder from 'images/magic-folder.svg'
import customUnescape from 'utils/customUnescape'
import ExternalSvgIcon, { IconType } from 'components/Dashboard/CommonComponents/ExternalSvgIcon'
import { foregroundStyle } from 'components/Dashboard/PlaceholderElements'
import TextWithOverFlowAndTippyPopup from 'components/TextWithOverFlowAndTippyPopup'

export default function GroupMenu({ isLoading }: { isLoading?: boolean }): ReactElement {
  const currentGroupData = useAppSelector(s => s.groups.currentGroup)

  const { data: proxiesData } = useGetProxiesQuery('')
  const redirectLocation = proxiesData?.proxies?.find(p => p.PK === currentGroupData?.action.via)
  const isBlockOrWhitelistRuleType =
    currentGroupData?.action?.do === RuleType.BLOCK ||
    currentGroupData?.action?.do === RuleType.WHITELIST

  const groupDetails = useMemo(() => {
    const { action, count = 0, is_magic } = currentGroupData ?? {}
    switch (action?.do) {
      case RuleType.BLOCK:
        return { icon: BlockFolder, description: `${count} Block Rule${count !== 1 ? 's' : ''}` }
      case RuleType.SPOOF_IP:
        return {
          icon: RedirectFolder,
          description: `${count} Redirect Rule${count !== 1 ? 's' : ''}`,
        }
      case RuleType.SPOOF_TAG:
        return {
          icon: RedirectFolder,
          description: `${count} Redirect (${
            redirectLocation?.city ||
            (action?.via === '?'
              ? 'a random location'
              : action?.via === 'LOCAL'
              ? 'Auto'
              : action?.via)
          }) Rule${count !== 1 ? 's' : ''}`,
        }
      case RuleType.WHITELIST:
        return {
          icon: BypassFolder,
          description: `${count} Bypass Rule${count !== 1 ? 's' : ''} `,
        }
      default:
        return {
          icon: !!is_magic ? MagicFolder : NoRuleFolder,
          description: `${count} rule${count !== 1 ? 's' : ''}`,
        }
    }
  }, [currentGroupData, redirectLocation])

  return (
    <Flex
      sx={{
        position: ['static', 'relative'],
        width: ['100%', 'inherit'],
        alignItems: 'center',
      }}
    >
      {isLoading ? (
        <LoadingPlaceholder />
      ) : (
        <>
          <BackButton />
          <CustomRulesTitle>
            {customUnescape(currentGroupData?.group) && (
              <Flex sx={{ alignItems: 'center' }}>
                <Flex
                  sx={{ position: 'relative', mr: '1.2rem', alignItems: 'center', flexShrink: 0 }}
                >
                  <Svg
                    svg={groupDetails.icon}
                    fill={isBlockOrWhitelistRuleType ? undefined : 'aliceBlue'}
                    sx={{
                      width: '3.2rem',
                      height: '3.2rem',
                      // folder icon should have the right color for the dark/light mode
                      ...(isBlockOrWhitelistRuleType && {
                        '& path:first-of-type': {
                          fill: 'aliceBlue',
                        },
                      }),
                    }}
                    descriptiveText={`${currentGroupData?.action?.do || 'Empty'} Folder Icon`}
                  />
                  {redirectLocation?.country && (
                    <ExternalSvgIcon
                      icon={redirectLocation?.country || ''}
                      type={IconType.LOCATIONS}
                      sx={{
                        position: 'absolute',
                        bottom: '0',
                        right: '0',
                        width: '1.8rem',
                        height: '1.8rem',
                      }}
                      descriptiveText={`${redirectLocation?.city}, ${redirectLocation?.country} Flag`}
                    />
                  )}
                </Flex>
                <Flex sx={{ flexDirection: 'column' }}>
                  <TextWithOverFlowAndTippyPopup
                    shouldResetParentSettings
                    ariaLabel="group name"
                    variant="size15Weight700"
                    sxText={{ color: 'aliceBlue', mb: '0.2rem' }}
                    content={customUnescape(currentGroupData?.group)}
                  />
                  <TextWithOverFlowAndTippyPopup
                    shouldResetParentSettings
                    variant="size12Weight400"
                    testId="group-description"
                    ariaLabel="group description"
                    placement="bottom"
                    sxText={{
                      color: 'aliceBlue60',
                    }}
                    content={groupDetails.description}
                  />
                </Flex>
              </Flex>
            )}
          </CustomRulesTitle>
        </>
      )}
    </Flex>
  )
}

function LoadingPlaceholder(): ReactElement {
  return (
    <Flex sx={{ flexDirection: 'row', gap: '1.2rem', alignItems: 'center', flexShrink: 0 }}>
      <Box
        sx={{
          ...foregroundStyle,
          width: '4rem',
          height: '4rem',
        }}
      />
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '0.6rem',
        }}
      >
        <Box
          sx={{
            ...foregroundStyle,
            width: '10rem',
            height: '1.5rem',
          }}
        />
        <Box
          sx={{
            ...foregroundStyle,
            width: '16rem',
            height: '1.4rem',
          }}
        />
      </Flex>
    </Flex>
  )
}
